import CookieType, { CookieOptions } from "@constants/CookieType";
import { type default as CurrencyType } from "@interfaces/Currency";
import type { EligibleAffiliate } from "@interfaces/LandingPricing";
import { encryptedStorage, hashKey } from "@utilities/encrypted-storage";
import { createHydrationGuard } from "@utilities/hydration-guard";
import { trackMetaSearch } from "@utilities/meta-pixel";
import Cookies from "js-cookie";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface ConfigurationState {
  currency: string;
  currencies: CurrencyType[];
  affiliate: string;
  // Metadata for affiliates resolved at runtime via /affiliate_code_check/
  // (i.e. the `?a=CODE` flow). JSON-listed affiliates use the build-time
  // entry in `eligible_affiliates` instead — see `useAffiliate()`.
  affiliateMeta: EligibleAffiliate | null;
  couponCode: string;
  countryCodeFromApi: string;

  lastVisitedRoute: string;
  lastVisitedLocationTab: number;

  searchHistory: string[];

  isNeededToShowRestrictedToPurchasePopup: boolean;
  showRestrictedToPurchasePopup: boolean;

  // Once true, the cookie consent bar must never be shown again — persisted in
  // (encrypted) localStorage so it survives forever, unlike a cookie.
  hasAcceptedCookieConsent: boolean;

  lastPromoId: string;

  updateCurrency: (value: string) => void;
  resetCurrency: () => void;
  setCurrencies: (value: CurrencyType[]) => void;
  updateAffiliate: (value: string) => void;
  setAffiliateMeta: (value: EligibleAffiliate | null) => void;
  updateCouponCode: (value: string) => void;
  resetCouponCode: () => void;
  setCountryCodeFromApi: (value: string) => void;

  resetLastVisitedRoute: () => void;
  updateLastVisitedRoute: (value: string) => void;
  updateLastVisitedLocationTab: (value: number) => void;

  addSearchHistory: (value: string) => void;
  removeSearchHistory: (value: string) => void;
  clearSearchHistory: () => void;

  setIsNeededToShowRestrictedToPurchasePopup: (value: boolean) => void;
  setShowRestrictedToPurchasePopup: (value: boolean) => void;

  setHasAcceptedCookieConsent: (value: boolean) => void;

  setLastPromoId: (value: string) => void;

  reset: () => void;
}

const SEARCH_HISTORY_LIMIT = 8;

const hydrationGuard = createHydrationGuard<ConfigurationState>();

export const useConfigurationStore = create<ConfigurationState>()(
  persist(
    hydrationGuard.track((set, get) => ({
      currency: process.env.GATSBY_DEFAULT_CURRENCY ?? "EUR",
      currencies: [],
      affiliate: process.env.GATSBY_AFFILIATE_CODE ?? "",
      affiliateMeta: null,
      couponCode: "",
      countryCodeFromApi: "",

      lastVisitedRoute: "",
      lastVisitedLocationTab: 1, // country tab

      searchHistory: [],

      isNeededToShowRestrictedToPurchasePopup: false,
      showRestrictedToPurchasePopup: false,

      hasAcceptedCookieConsent: false,

      lastPromoId: Cookies.get(CookieType.LAST_PROMO_ID) ?? "",

      updateCurrency: (value: string) => {
        if (!value || !get().currencies.some(item => item.currency === value)) {
          return;
        }
        set({
          currency: value,
        });
        Cookies.set(CookieType.CURRENCY, value, CookieOptions);
      },
      resetCurrency: () => {
        set({
          currency: process.env.GATSBY_DEFAULT_CURRENCY ?? "EUR",
        });
      },
      setCurrencies: (value: CurrencyType[]) => {
        set({
          currencies: value,
        });
        if (!value.some(item => item.currency === get().currency)) {
          get().updateCurrency(value[0].currency);
        }
      },
      updateAffiliate: (value: string) => {
        if (!value) {
          return;
        }
        set({
          affiliate: value,
        });
        Cookies.set(CookieType.AFFILIATE, value, {
          ...CookieOptions,
          expires: 15,
        });
      },
      setAffiliateMeta: (value: EligibleAffiliate | null) => {
        set({
          affiliateMeta: value,
        });
      },
      updateCouponCode: (value: string) => {
        if (!value) {
          return;
        }
        set({
          couponCode: value,
        });
      },
      resetCouponCode: () => {
        set({
          couponCode: "",
        });
      },
      setCountryCodeFromApi: (value: string) => {
        set({
          countryCodeFromApi: value,
        });
      },
      resetLastVisitedRoute: () => {
        set({
          lastVisitedRoute: "",
        });
      },
      updateLastVisitedRoute: (value: string) => {
        set({
          lastVisitedRoute: value,
        });
      },
      updateLastVisitedLocationTab: (value: number) => {
        set({
          lastVisitedLocationTab: value,
        });
      },
      addSearchHistory: (value: string) => {
        const trimmed = value.trim();
        if (trimmed === "") {
          return;
        }
        const lower = trimmed.toLowerCase();
        const current = get().searchHistory;
        const isRedundant = current.some(item => {
          const itemLower = item.toLowerCase();
          return itemLower !== lower && itemLower.startsWith(lower);
        });
        if (isRedundant) {
          return;
        }
        const filtered = current.filter(item => {
          const itemLower = item.toLowerCase();
          if (itemLower === lower) return false;
          if (lower.startsWith(itemLower)) return false;
          return true;
        });
        set({
          searchHistory: [trimmed, ...filtered].slice(0, SEARCH_HISTORY_LIMIT),
        });
        trackMetaSearch(trimmed);
      },
      removeSearchHistory: (value: string) => {
        set({
          searchHistory: get().searchHistory.filter(item => item !== value),
        });
      },
      clearSearchHistory: () => {
        set({
          searchHistory: [],
        });
      },
      setIsNeededToShowRestrictedToPurchasePopup: (value: boolean) => {
        set({
          isNeededToShowRestrictedToPurchasePopup: value,
        });
      },
      setShowRestrictedToPurchasePopup: (value: boolean) => {
        set({
          showRestrictedToPurchasePopup: value,
        });
      },
      setHasAcceptedCookieConsent: (value: boolean) => {
        set({
          hasAcceptedCookieConsent: value,
        });
      },
      setLastPromoId: (value: string) => {
        if (!value) {
          return;
        }
        set({
          lastPromoId: value,
        });
        Cookies.set(CookieType.LAST_PROMO_ID, value, CookieOptions);
      },
      reset: () => {
        set({
          currency: process.env.GATSBY_DEFAULT_CURRENCY ?? "EUR",
          affiliate: process.env.GATSBY_AFFILIATE_CODE ?? "",
          affiliateMeta: null,
          couponCode: "",
        });
      },
    })),
    {
      name: hashKey("configuration-storage"),
      skipHydration: true,
      storage: createJSONStorage(() =>
        hydrationGuard.storage(encryptedStorage),
      ),
      merge: hydrationGuard.merge,
    },
  ),
);

export const useConfigurationState = (): ConfigurationState => {
  return useConfigurationStore(state => {
    return state;
  });
};
